import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleMap, Polygon, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import {
  createSector,
  deleteSector,
  fetchSectors,
  updateSector
} from '../../redux/slices/sectorsSlice';

const DEFAULT_CENTER = { lat: 53.5511, lng: 9.9937 };
const DEFAULT_COLOR = '#2E7D32';
const MAP_CONTAINER_STYLE = { width: '100%', height: '620px' };

const emptyForm = {
  id: null,
  name: '',
  code: '',
  description: '',
  color: DEFAULT_COLOR,
  active: true,
  dispatchFromQueueEnd: false,
  sortOrder: 0,
  priority: 100,
  polygon: []
};

const calculateBounds = (polygon) => {
  if (!polygon.length) return null;
  return polygon.reduce((bounds, point) => ({
    north: Math.max(bounds.north, point.lat),
    south: Math.min(bounds.south, point.lat),
    east: Math.max(bounds.east, point.lng),
    west: Math.min(bounds.west, point.lng)
  }), {
    north: polygon[0].lat,
    south: polygon[0].lat,
    east: polygon[0].lng,
    west: polygon[0].lng
  });
};

const calculateCentroid = (polygon) => {
  if (!polygon.length) return null;
  const totals = polygon.reduce((acc, point) => ({
    lat: acc.lat + point.lat,
    lng: acc.lng + point.lng
  }), { lat: 0, lng: 0 });
  return {
    lat: totals.lat / polygon.length,
    lng: totals.lng / polygon.length
  };
};

const hasSelfIntersection = (polygon) => {
  if (polygon.length < 4) return false;

  const orientation = (a, b, c) => {
    const value = (b.lng - a.lng) * (c.lat - b.lat) - (b.lat - a.lat) * (c.lng - b.lng);
    if (Math.abs(value) < 1e-12) return 0;
    return value > 0 ? 1 : 2;
  };

  const intersects = (p1, q1, p2, q2) => {
    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);
    return o1 !== o2 && o3 !== o4;
  };

  const edges = polygon.map((point, index) => ({
    start: point,
    end: polygon[(index + 1) % polygon.length],
    index
  }));

  for (let i = 0; i < edges.length; i += 1) {
    for (let j = i + 1; j < edges.length; j += 1) {
      const areAdjacent = Math.abs(edges[i].index - edges[j].index) === 1 ||
        (edges[i].index === 0 && edges[j].index === edges.length - 1);
      if (areAdjacent) continue;
      if (intersects(edges[i].start, edges[i].end, edges[j].start, edges[j].end)) {
        return true;
      }
    }
  }
  return false;
};

const SectorManagementPage = () => {
  const dispatch = useDispatch();
  const polygonRef = useRef(null);
  const { sectors, isLoading, isSaving, error } = useSelector(state => state.sectors);

  const [form, setForm] = useState(emptyForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectorToDelete, setSectorToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: window.GOOGLE_MAPS_LIBRARIES || ['geometry']
  });

  useEffect(() => {
    dispatch(fetchSectors());
  }, [dispatch]);

  const activeSectorCount = useMemo(
    () => sectors.filter(sector => sector.active !== false).length,
    [sectors]
  );

  const mapCenter = useMemo(() => {
    if (form.polygon.length) return calculateCentroid(form.polygon);
    const firstSectorWithCentroid = sectors.find(sector => sector.centroid);
    return firstSectorWithCentroid?.centroid || DEFAULT_CENTER;
  }, [form.polygon, sectors]);

  const validationError = useMemo(() => {
    if (!form.name.trim()) return 'Name ist erforderlich';
    if (!form.code.trim()) return 'Sektornummer ist erforderlich';
    if (form.polygon.length < 3) return 'Mindestens 3 Polygonpunkte erforderlich';
    if (hasSelfIntersection(form.polygon)) return 'Polygon darf sich nicht selbst schneiden';
    return null;
  }, [form]);

  const handleMapClick = (event) => {
    if (!event.latLng) return;
    setForm(prev => ({
      ...prev,
      polygon: [
        ...prev.polygon,
        {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
      ]
    }));
  };

  const syncPolygonFromMap = () => {
    if (!polygonRef.current) return;
    const path = polygonRef.current.getPath();
    const nextPolygon = [];
    for (let i = 0; i < path.getLength(); i += 1) {
      const point = path.getAt(i);
      nextPolygon.push({ lat: point.lat(), lng: point.lng() });
    }
    setForm(prev => ({ ...prev, polygon: nextPolygon }));
  };

  const handleEdit = (sector) => {
    setForm({
      id: sector.id,
      name: sector.name || '',
      code: sector.code || '',
      description: sector.description || '',
      color: sector.color || DEFAULT_COLOR,
      active: sector.active !== false,
      dispatchFromQueueEnd: sector.dispatchFromQueueEnd === true,
      sortOrder: sector.sortOrder || 0,
      priority: sector.priority || 100,
      polygon: Array.isArray(sector.polygon) ? sector.polygon : []
    });
  };

  const handleNew = () => {
    polygonRef.current = null;
    setForm(emptyForm);
  };

  const handleUndoPoint = () => {
    setForm(prev => ({ ...prev, polygon: prev.polygon.slice(0, -1) }));
  };

  const handleClearPolygon = () => {
    polygonRef.current = null;
    setForm(prev => ({ ...prev, polygon: [] }));
  };

  const handleSave = async () => {
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'warning' });
      return;
    }

    const payload = {
      name: form.name,
      code: form.code,
      description: form.description,
      color: form.color || DEFAULT_COLOR,
      active: form.active,
      dispatchFromQueueEnd: form.dispatchFromQueueEnd,
      sortOrder: Number(form.sortOrder) || 0,
      priority: Number(form.priority) || 100,
      polygon: form.polygon,
      bounds: calculateBounds(form.polygon),
      centroid: calculateCentroid(form.polygon)
    };

    const result = form.id
      ? await dispatch(updateSector({ id: form.id, ...payload }))
      : await dispatch(createSector(payload));

    if (result.error) {
      setSnackbar({ open: true, message: result.payload || result.error.message, severity: 'error' });
      return;
    }

    setSnackbar({ open: true, message: 'Sektor wurde gespeichert', severity: 'success' });
    dispatch(fetchSectors());
    if (!form.id) handleNew();
  };

  const handleDeleteClick = (sector) => {
    setSectorToDelete(sector);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sectorToDelete) return;
    const result = await dispatch(deleteSector(sectorToDelete.id));
    if (result.error) {
      setSnackbar({ open: true, message: result.payload || result.error.message, severity: 'error' });
    } else {
      setSnackbar({ open: true, message: 'Sektor wurde gelöscht', severity: 'success' });
      if (form.id === sectorToDelete.id) handleNew();
    }
    setDeleteDialogOpen(false);
    setSectorToDelete(null);
  };

  if (isLoading) {
    return <Loading message="Sektoren werden geladen..." />;
  }

  if (error && sectors.length === 0) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Sektoren"
        message={error}
        onRetry={() => dispatch(fetchSectors())}
      />
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Sektoren verwalten"
        description="Fahrgebiet als präzise Polygon-Sektoren zeichnen und verwalten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Einstellungen', path: '/einstellungen' },
          { label: 'Sektoren', path: '/einstellungen/sektoren' }
        ]}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Logik der Sektor-Disposition</AlertTitle>
        <Typography variant="body2">
          Fahrer werden immer genau einem aktiven Sektor zugeordnet. Wenn sich gezeichnete Sektoren überlappen,
          gewinnt der Sektor mit der höheren Priorität; bei gleicher Priorität entscheidet die niedrigere Sortierung.
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2">
            Der Gewinner-Sektor ist in der Fahrer-App grün und bestimmt die Warteschlange.
          </Typography>
          <Typography component="li" variant="body2">
            Weitere überlappende Sektoren werden in der Fahrer-App gelb angezeigt, ändern aber nicht die Anmeldung.
          </Typography>
          <Typography component="li" variant="body2">
            Nur aktive Sektoren werden für Fahrer-App, Warteschlange und automatische Vermittlung berücksichtigt.
          </Typography>
        </Box>
      </Alert>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">Karteneditor</Typography>
                <Typography variant="body2" color="text.secondary">
                  Auf die Karte klicken, um Polygonpunkte zu setzen. Gespeicherte Polygone sind halbtransparent sichtbar.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Einzelne Punkte können nachträglich verschoben werden; das gesamte Polygon kann bewusst nicht als Fläche verschoben werden.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ein Sektor benötigt mindestens 3 Punkte. Selbstüberschneidungen werden beim Speichern verhindert.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Letzten Punkt entfernen">
                  <span>
                    <IconButton onClick={handleUndoPoint} disabled={!form.polygon.length}>
                      <UndoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Polygon leeren">
                  <span>
                    <IconButton onClick={handleClearPolygon} disabled={!form.polygon.length}>
                      <ClearIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {loadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Google Maps konnte nicht geladen werden.
              </Alert>
            )}

            {!isLoaded ? (
              <Loading message="Karte wird geladen..." />
            ) : (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={mapCenter}
                zoom={12}
                onClick={handleMapClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true
                }}
              >
                {sectors.map(sector => (
                  sector.id !== form.id && Array.isArray(sector.polygon) && sector.polygon.length >= 3 ? (
                    <Polygon
                      key={sector.id}
                      paths={sector.polygon}
                      options={{
                        fillColor: sector.color || DEFAULT_COLOR,
                        fillOpacity: sector.active === false ? 0.08 : 0.18,
                        strokeColor: sector.color || DEFAULT_COLOR,
                        strokeOpacity: sector.active === false ? 0.35 : 0.75,
                        strokeWeight: 2,
                        clickable: false
                      }}
                    />
                  ) : null
                ))}

                {form.polygon.length > 0 && (
                  <>
                    <Polygon
                      paths={form.polygon}
                      editable
                      onLoad={(polygon) => {
                        polygonRef.current = polygon;
                      }}
                      onMouseUp={syncPolygonFromMap}
                      options={{
                        fillColor: form.color || DEFAULT_COLOR,
                        fillOpacity: 0.28,
                        strokeColor: form.color || DEFAULT_COLOR,
                        strokeOpacity: 0.95,
                        strokeWeight: 3
                      }}
                    />
                    {form.polygon.map((point, index) => (
                      <Marker
                        key={`${point.lat}-${point.lng}-${index}`}
                        position={point}
                        label={`${index + 1}`}
                      />
                    ))}
                  </>
                )}
              </GoogleMap>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapIcon color="primary" />
                  <Typography variant="h6">
                    {form.id ? 'Sektor bearbeiten' : 'Neuer Sektor'}
                  </Typography>
                </Box>
                <Button startIcon={<AddIcon />} onClick={handleNew}>
                  Neu
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 7 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={form.name}
                    onChange={(event) => setForm(prev => ({ ...prev, name: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    fullWidth
                    label="Sektornummer"
                    value={form.code}
                    onChange={(event) => setForm(prev => ({ ...prev, code: event.target.value }))}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Beschreibung"
                    value={form.description}
                    onChange={(event) => setForm(prev => ({ ...prev, description: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Farbe"
                    type="color"
                    value={form.color}
                    onChange={(event) => setForm(prev => ({ ...prev, color: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Sortierung"
                    type="number"
                    value={form.sortOrder}
                    onChange={(event) => setForm(prev => ({ ...prev, sortOrder: event.target.value }))}
                    helperText="Niedrigerer Wert gewinnt, wenn die Priorität gleich ist."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Priorität"
                    type="number"
                    value={form.priority}
                    onChange={(event) => setForm(prev => ({ ...prev, priority: event.target.value }))}
                    helperText="Höherer Wert gewinnt bei überlappenden Sektoren."
                  />
                </Grid>
                <Grid size={12}>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.active}
                          onChange={(event) => setForm(prev => ({ ...prev, active: event.target.checked }))}
                        />
                      }
                      label="Aktiv"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.dispatchFromQueueEnd}
                          onChange={(event) => setForm(prev => ({
                            ...prev,
                            dispatchFromQueueEnd: event.target.checked
                          }))}
                        />
                      }
                      label="Letztes Taxi zuerst disponieren"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                      Für Bahnhof, Flughafen oder Taxistände: Die sichtbare Position bleibt unverändert,
                      die automatische Angebotsreihenfolge beginnt intern hinten.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Polygonpunkte: {form.polygon.length}
                </Typography>
                <Chip
                  size="small"
                  label={validationError || 'Speicherbereit'}
                  color={validationError ? 'warning' : 'success'}
                  variant={validationError ? 'outlined' : 'filled'}
                />
              </Stack>

              <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                Änderungen an aktiven Sektoren wirken nach dem Speichern auf Fahrer-App, Warteschlange und Sektor-Disposition.
                Für produktive Daten ist Deaktivieren meist sicherer als Löschen.
              </Alert>

              <Button
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {form.id ? 'Sektor speichern' : 'Sektor anlegen'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Sektoren</Typography>
              <Chip label={`${activeSectorCount}/${sectors.length} aktiv`} color="primary" size="small" />
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nr.</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Punkte</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectors.map(sector => (
                    <TableRow key={sector.id} selected={sector.id === form.id}>
                      <TableCell>
                        <Chip
                          label={sector.code || '-'}
                          size="small"
                          sx={{ backgroundColor: sector.color || DEFAULT_COLOR, color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{sector.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sector.active === false ? 'Inaktiv' : 'Aktiv'}
                        </Typography>
                        {sector.dispatchFromQueueEnd === true && (
                          <Chip
                            label="Letztes Taxi zuerst"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">{sector.polygon?.length || 0}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(sector)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(sector)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sectors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Noch keine Sektoren angelegt.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Aktivierte Sektoren werden von der Fahrer-App geladen. Inaktive Sektoren bleiben zur Dokumentation erhalten,
              werden aber nicht für Anmeldung, Warteschlange oder automatische Vermittlung verwendet.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Sektor löschen?"
        content={`Soll der Sektor "${sectorToDelete?.name || ''}" wirklich gelöscht werden? Für produktive Daten ist Deaktivieren meist sicherer als Löschen.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SectorManagementPage;
